<template>
  <div>
    <div class="session-timeout-container">
      <!-- Warning notification for 5 minutes before timeout -->
      <transition name="slide-fade">
        <div
          v-if="showFiveMinuteWarning && !showOneMinuteWarning"
          class="timeout-warning five-minute-warning"
        >
          <div class="warning-content">
            <span class="warning-icon">⚠️</span>
            <span class="warning-text">{{
              $t("Your session will expire in 5 minutes due to inactivity")
            }}</span>
          </div>
        </div>
      </transition>

      <!-- Warning notification for 1 minute before timeout -->
      <transition name="slide-fade">
        <div
          v-if="showOneMinuteWarning"
          class="timeout-warning one-minute-warning"
        >
          <div class="warning-content">
            <span class="warning-icon">⚠️</span>
            <span class="warning-text">{{
              $t("Your session will expire in 1 minute due to inactivity")
            }}</span>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import { debounce } from "lodash";

export default {
  name: "SessionTimeoutManager",
  data() {
    return {
      // Configuration
      timeoutMinutes: 10,
      timeoutEnabled: true,

      // Timers
      statusCheckInterval: null,
      sessionExpiryTimer: null,

      // UI states
      showFiveMinuteWarning: false,
      showOneMinuteWarning: false,

      // Session tracking
      sessionExpiryTime: null,

      // Flag to prevent further API calls after session timeout
      isSessionTimedOut: false,

      // Token refresh lock to prevent concurrent refreshes
      isRefreshingToken: false,
      
      // Flag to track if user interacted after warning was shown
      interactedAfterWarning: false,
      
      // Timestamp of last successful token refresh
      lastTokenRefresh: Date.now()
    };
  },

  created() {
    // Get timeout configuration from the server
    this.getSessionConfiguration();

    // Listen for session timeout events from axios interceptors
    document.addEventListener(
      "session-timeout",
      this.handleSessionTimeoutEvent
    );
  },

  mounted() {
    // Setup session monitoring
    this.setupSessionMonitoring();

    // Track initial activity
    if (localStorage.getItem("isAuthenticated") === "true") {
      window.sessionStorage.setItem('lastActivity', Date.now().toString());
      window.sessionStorage.setItem('wasActive', 'true');
    }
  },

  beforeDestroy() {
    this.cleanup();
    document.removeEventListener(
      "session-timeout",
      this.handleSessionTimeoutEvent
    );
  },

  methods: {
    getSessionConfiguration() {
      // Skip if session is timed out or user not authenticated
      if (
        this.isSessionTimedOut ||
        localStorage.getItem("isAuthenticated") !== "true"
      ) {
        return;
      }

      axios
        .get("session/config")
        .then((response) => {
          if (response.data && response.data.status === "OK") {
            this.timeoutMinutes = response.data.timeout_minutes || 10;
            this.timeoutEnabled = response.data.timeout_enabled !== false;

            // Initialize session monitoring after getting config
            this.checkSessionStatus();
          }
        })
        .catch((error) => {
          // Only log critical errors
          if (error.response && error.response.status === 401) {
            this.markSessionTimedOut();
          }
        });
    },

    setupSessionMonitoring() {
      // Skip if session is already timed out
      if (this.isSessionTimedOut) {
        return;
      }

      // Add user activity listeners
      this.addActivityListeners();

      // Start periodic session status check (every 15 seconds)
      // This is less frequent than before to reduce unnecessary API calls
      this.statusCheckInterval = setInterval(() => {
        // Skip interval check if session is timed out
        if (!this.isSessionTimedOut) {
          this.checkSessionStatus();
        } else {
          // If session is timed out, clear the interval
          this.cleanup();
        }
      }, 15000);

      // Initial check
      this.checkSessionStatus();
    },

    checkSessionStatus() {
      // Skip if session is timed out or user not authenticated
      if (
        this.isSessionTimedOut ||
        localStorage.getItem("isAuthenticated") !== "true"
      ) {
        return;
      }

      // Reset interaction flag when checking status
      this.interactedAfterWarning = false;
      
      axios
        .get("session/status")
        .then((response) => {
          if (response.data.status === "OK" && response.data.token_info) {
            const { remaining_seconds } = response.data.token_info;

            if (remaining_seconds !== null) {
              // Update session expiry time
              this.sessionExpiryTime = new Date(
                Date.now() + remaining_seconds * 1000
              );

              // Update UI warnings based on remaining time
              this.updateWarnings(remaining_seconds);

              // Set up expiry timer
              this.setupExpiryTimer(remaining_seconds);
            }
          }
        })
        .catch((error) => {
          // Only handle 401 errors
          if (error.response && error.response.status === 401) {
            // Check if user is still active
            const lastActivity = window.sessionStorage.getItem('lastActivity');
            const now = Date.now();
            
            // If there was activity in the last minute, this is likely a false timeout
            if (lastActivity && (now - parseInt(lastActivity)) < 60000) {
              return;
            }
            
            // Otherwise, mark as a session timeout
            this.markSessionTimedOut();
          }
        });
    },

    updateWarnings(remainingSeconds) {
      // Skip if session is timed out
      if (this.isSessionTimedOut) {
        return;
      }

      // Store previous warning states to detect changes
      const hadWarningBefore = this.showFiveMinuteWarning || this.showOneMinuteWarning;
      
      // Clear existing warning states
      this.showFiveMinuteWarning = false;
      this.showOneMinuteWarning = false;

      // Update warnings based on remaining time
      if (remainingSeconds <= 60) {
        // Less than or equal to 1 minute
        this.showOneMinuteWarning = true;
      } else if (remainingSeconds <= 300) {
        // Between 1 and 5 minutes
        this.showFiveMinuteWarning = true;
      }
      
      // If we just started showing a warning, reset the interaction flag
      if (!hadWarningBefore && (this.showFiveMinuteWarning || this.showOneMinuteWarning)) {
        this.interactedAfterWarning = false;
      }
      
      // If we had warnings before but don't anymore (remaining time > 5 min), 
      // clear the interaction flag as well
      if (hadWarningBefore && !(this.showFiveMinuteWarning || this.showOneMinuteWarning)) {
        this.interactedAfterWarning = false;
      }
    },

    setupExpiryTimer(remainingSeconds) {
      // Skip if session is timed out
      if (this.isSessionTimedOut) {
        return;
      }

      // Clear any existing timer
      if (this.sessionExpiryTimer) {
        clearTimeout(this.sessionExpiryTimer);
      }

      // Set a new timer for session expiry
      if (remainingSeconds > 0) {
        this.sessionExpiryTimer = setTimeout(() => {
          // Mark session as timed out
          this.markSessionTimedOut();

          // Emit session-timeout event to parent
          this.$emit("session-timeout");

          // Clear warnings
          this.showFiveMinuteWarning = false;
          this.showOneMinuteWarning = false;
        }, remainingSeconds * 1000);
      }
    },

    addActivityListeners() {
      // Track user activity with key events that indicate user presence
      const activityEvents = [
        "mousedown",
        "mousemove",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];

      // Use debounced function to prevent too many refresh calls
      this.handleUserActivity = debounce(this.onUserActivity, 300);

      // Add all event listeners
      activityEvents.forEach((event) => {
        document.addEventListener(event, this.handleUserActivity);
      });
    },

    removeActivityListeners() {
      const activityEvents = [
        "mousedown",
        "mousemove",
        "keydown",
        "scroll",
        "touchstart",
        "click",
      ];

      // If the handler exists, remove it from all events
      if (this.handleUserActivity) {
        activityEvents.forEach((event) => {
          document.removeEventListener(event, this.handleUserActivity);
        });
      }
    },

    onUserActivity() {
      // Skip if session is timed out
      if (this.isSessionTimedOut) {
        return;
      }

      // Update last activity timestamp for race condition detection
      window.sessionStorage.setItem('lastActivity', Date.now().toString());
      window.sessionStorage.setItem('wasActive', 'true');

      // Only refresh token if:
      // 1. A warning is currently shown (indicating session is about to expire)
      // 2. User hasn't already interacted since the warning was shown
      if ((this.showFiveMinuteWarning || this.showOneMinuteWarning) && !this.interactedAfterWarning) {
        this.interactedAfterWarning = true;
        this.refreshToken();
      }
    },

    refreshToken() {
      // Skip if session is timed out or user not authenticated
      if (
        this.isSessionTimedOut ||
        localStorage.getItem("isAuthenticated") !== "true" ||
        this.isRefreshingToken ||
        !this.timeoutEnabled
      ) {
        return;
      }
      
      // Check for recent refreshes to prevent redundant calls
      const now = Date.now();
      if (now - this.lastTokenRefresh < 5000) {
        return;
      }
      
      // Set refresh lock
      this.isRefreshingToken = true;
      
      axios
        .post("session/refresh")
        .then((response) => {
          if (response.data.status === "OK" && response.data.access_token) {
            // Update token in storage
            localStorage.setItem("authToken", response.data.access_token);
            
            // Update timestamp of last successful refresh
            this.lastTokenRefresh = Date.now();
            
            // If expiration time was provided, update our tracking
            if (response.data.expires_at) {
              this.sessionExpiryTime = new Date(response.data.expires_at);
              const remainingMs = this.sessionExpiryTime - Date.now();
              const remainingSeconds = Math.floor(remainingMs / 1000);
              
              // Clear warnings since we've successfully refreshed
              this.showFiveMinuteWarning = false;
              this.showOneMinuteWarning = false;
              
              // Update expiry timer
              this.setupExpiryTimer(remainingSeconds);
            }
          }
        })
        .catch((error) => {
          // Handle 401 - session already expired
          if (error.response && error.response.status === 401) {
            this.markSessionTimedOut();
          }
        })
        .finally(() => {
          this.isRefreshingToken = false;
        });
    },

    // Handler for session timeout event from document
    handleSessionTimeoutEvent() {
      this.markSessionTimedOut();
    },

    // Mark session as timed out and clean up
    markSessionTimedOut() {
      // Prevent duplicate timeout handling
      if (this.isSessionTimedOut) {
        return;
      }
      
      // Set the timeout flag
      this.isSessionTimedOut = true;

      // Clear warnings
      this.showFiveMinuteWarning = false;
      this.showOneMinuteWarning = false;

      // Clean up all timers and listeners
      this.cleanup();

      // Emit session-timeout event to App component
      this.$emit("session-timeout");
    },

    cleanup() {
      // Remove all event listeners
      this.removeActivityListeners();

      // Clear all timers
      if (this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval);
        this.statusCheckInterval = null;
      }

      if (this.sessionExpiryTimer) {
        clearTimeout(this.sessionExpiryTimer);
        this.sessionExpiryTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.session-timeout-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  width: 100%;
}

.timeout-warning {
  position: fixed;
  bottom: 24px;
  left: 24px;
  background-color: #ff9800;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 320px;
  z-index: 9999;
  pointer-events: auto;
}

.warning-content {
  display: flex;
  align-items: center;
}

.warning-icon {
  margin-right: 10px;
  font-size: 18px;
}

.warning-text {
  font-size: 14px;
  line-height: 1.4;
}

.one-minute-warning {
  background-color: #f44336;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .timeout-warning {
    left: 16px;
    right: 16px;
    bottom: 16px;
    max-width: none;
    width: calc(100% - 32px);
  }
}
</style>
